import { rem } from 'polished'
import type { ReactNode } from 'react'
import { memo, useCallback } from 'react'
import styled from 'styled-components'

import type { ItemCardVariant } from '../types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import OutsideTripDatesIcon from 'tripPlanner/components/Itinerary/OutsideTripDatesIcon'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import type { TripItem } from 'tripPlanner/types/tripItem'

interface Props {
  flightDate?: string
  flightTime?: string
  airportCode?: ReactNode
  item: TripItem
  canShowWarning?: boolean
  variant: ItemCardVariant
}

const Container = styled(VerticalSpacer)`
  & *:empty {
    display: none;
  }
`

const OutsideTripDatesIconContainer = styled.div`
  *:empty {
    display: block;
  }
  position: absolute;
  inset-inline-start: ${rem(72)};
  bottom: ${rem(-4)};
`

const DateContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`

function FlightLocationGroup({
  flightDate,
  flightTime,
  airportCode,
  item,
  canShowWarning = false,
  variant,
}: Props) {
  const openModal = useOpenModal()

  const editItem = useCallback(
    () => openModal(EditTripItem, { item }),
    [item, openModal],
  )

  return (
    <Container gap={4}>
      <DateContainer>
        <BodyText variant="small">{flightDate}</BodyText>
        {canShowWarning && variant === 'tripSummary' && (
          <OutsideTripDatesIconContainer>
            <OutsideTripDatesIcon changeItemDates={editItem} item={item} />
          </OutsideTripDatesIconContainer>
        )}
      </DateContainer>
      <div>
        <Heading variant="heading6">{flightTime}</Heading>
        <BodyText variant="medium" colour="light-foreground-alt">
          {airportCode}
        </BodyText>
      </div>
    </Container>
  )
}

export default memo(FlightLocationGroup)
